import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useMode } from "../context/ModeContext";

/**
 * XEA Brand Logo — "The Spark Ring"
 *
 * A precise black ring with a tiny coral dot orbiting it. The dot animates
 * continuously on the dashboard / hero, and stays parked at 45° for static
 * surfaces (favicon, OG, email).
 *
 * Props:
 *   size      — pixel size of the mark (default 36)
 *   animated  — orbit the dot continuously (default true)
 *   wordmark  — show "XEA" beside the mark (default true)
 *   tagline   — show "XEA" subtitle (default false)
 *   tone      — "dark" (default, for bright backgrounds) | "light" (for dark)
 *   linkTo    — wraps the logo in a Link, "/" by default; pass null to disable
 */
export function Logo({
  size = 36,
  animated = true,
  wordmark = true,
  tagline = false,
  tone = "dark",
  linkTo = "/",
  className = "",
}) {
  const stroke = tone === "light" ? "#FFFFFF" : "#0A0A0A";
  let dotColor = "#FF4F27";
  let openPicker = null;
  let modeAccent = null;
  try {
    const m = useMode();
    openPicker = m.openPicker;
    modeAccent = m.config?.accent;
  } catch (_) {
    // Logo used outside ModeProvider (e.g. login page) — that's fine.
  }
  if (modeAccent) dotColor = modeAccent;

  // Mark — used both for static & animated variants.
  const Mark = (
    <span
      className="relative inline-flex shrink-0 items-center justify-center"
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="20" cy="20" r="14" stroke={stroke} strokeWidth="2.5" />
      </svg>

      {/* Orbiting spark */}
      {animated ? (
        <motion.span
          className="absolute"
          style={{
            width: size * 0.22,
            height: size * 0.22,
            top: "50%",
            left: "50%",
            marginLeft: -(size * 0.11),
            marginTop: -(size * 0.11),
            transformOrigin: "center center",
          }}
          animate={{ rotate: 360 }}
          transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
        >
          <span
            className="block rounded-full"
            style={{
              width: size * 0.22,
              height: size * 0.22,
              background: dotColor,
              transform: `translateX(${size * 0.39}px)`,
              boxShadow: `0 0 ${size * 0.4}px ${dotColor}`,
            }}
          />
        </motion.span>
      ) : (
        <span
          className="absolute block rounded-full"
          style={{
            width: size * 0.22,
            height: size * 0.22,
            background: dotColor,
            top: "20%",
            right: "8%",
            boxShadow: `0 0 ${size * 0.3}px ${dotColor}`,
          }}
        />
      )}
    </span>
  );

  const Wordmark = wordmark ? (
    <span className="flex flex-col leading-none">
      <span
        className={`font-display font-black tracking-[-0.04em] ${
          tone === "light" ? "text-white" : "text-ink-950"
        }`}
        style={{ fontSize: size * 0.6 }}
      >
        XEA
      </span>
      {tagline && (
        <span
          className={`mt-1 text-[10px] font-bold uppercase tracking-[0.22em] ${
            tone === "light" ? "text-white/60" : "text-ink-500"
          }`}
        >
          XEA
        </span>
      )}
    </span>
  ) : null;

  const content = (
    <span
      className={`inline-flex items-center gap-3 ${className}`}
      data-testid="ama-logo"
    >
      {Mark}
      {Wordmark}
    </span>
  );

  if (linkTo) {
    if (openPicker) {
      // Inside a ModeProvider — clicking the logo opens the studio switcher
      return (
        <button
          type="button"
          onClick={openPicker}
          data-testid="logo-mode-trigger"
          className="inline-flex bg-transparent border-0 cursor-pointer p-0"
          aria-label="Switch studio"
        >
          {content}
        </button>
      );
    }
    return (
      <Link to={linkTo} className="inline-flex">
        {content}
      </Link>
    );
  }
  return content;
}

export default Logo;
